import React from 'react';
import { Box, Container, Typography, Paper } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { ArrowBack as BackIcon } from '@mui/icons-material';
import { usePlatformSettings } from '../hooks/usePlatformSettings';

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const { settings: platformSettings } = usePlatformSettings();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#0a0a0a', py: 8 }}>
      <Container maxWidth="md">
        <Box 
          onClick={() => navigate('/')}
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            mb: 4, 
            cursor: 'pointer',
            color: '#00FFF5',
            '&:hover': { color: '#9D00FF' },
            transition: 'color 0.3s'
          }}
        >
          <BackIcon sx={{ mr: 1 }} />
          <Typography>Back to Home</Typography>
        </Box>

        <Paper sx={{ 
          p: 5, 
          bgcolor: '#171717', 
          border: '1px solid #262626',
          borderRadius: '12px'
        }}>
          <Typography 
            variant="h3" 
            sx={{ 
              mb: 2, 
              fontWeight: 700,
              background: 'linear-gradient(90deg, #00FFF5 0%, #9D00FF 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Privacy Policy
          </Typography>

          <Typography variant="body2" sx={{ mb: 4, color: '#737373' }}>
            Last Updated: November 14, 2025
          </Typography>

          {/* Introduction */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            1. Introduction
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            Welcome to {platformSettings.platformName}. We respect your privacy and are committed to protecting your personal data. 
            This privacy policy explains how we collect, use, and safeguard your information when you use our cognitive profiling service.
          </Typography>

          {/* Information We Collect */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            2. Information We Collect
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            2.1 Information You Provide
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            • <strong>Account Information:</strong> Email address, name, and profile picture (via Google Sign-In)
            <br />
            • <strong>Chat Logs:</strong> Conversation text you upload for analysis (1,000-50,000 words)
            <br />
            • <strong>Structured Data:</strong> Age, income trajectory, and other demographic information you choose to share
            <br />
            • <strong>Payment Information:</strong> Processed securely through Stripe (we do not store credit card details)
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            2.2 Automatically Collected Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            • <strong>Usage Data:</strong> Pages visited, features used, time spent on the platform
            <br />
            • <strong>Device Information:</strong> Browser type, operating system, IP address
            <br />
            • <strong>Authentication Tokens:</strong> Firebase authentication tokens to maintain your session
          </Typography>

          {/* How We Use Your Information */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We use your information to:
            <br />
            • Generate your personalized cognitive profile report
            <br />
            • Process your payments securely
            <br />
            • Provide customer support
            <br />
            • Improve our AI analysis algorithms (using anonymized data)
            <br />
            • Send you important service updates (not marketing)
            <br />
            • Ensure platform security and prevent fraud
          </Typography>

          {/* Data Storage and Security */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            4. Data Storage and Security
          </Typography>
          
          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            4.1 Chat Log Privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            Your chat logs are treated with extreme care:
            <br />
            • <strong>Temporary Storage:</strong> Chat logs are stored only during report generation
            <br />
            • <strong>One-Time Regeneration:</strong> Kept for 30 days to allow one free report regeneration
            <br />
            • <strong>Automatic Deletion:</strong> Automatically deleted after 30 days or after regeneration
            <br />
            • <strong>User Control:</strong> You can delete your report and associated data at any time
          </Typography>

          <Typography variant="h6" sx={{ mt: 3, mb: 1, color: '#00FFF5', fontWeight: 500 }}>
            4.2 Security Measures
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: '#a1a1aa', lineHeight: 1.8 }}>
            • All data is encrypted in transit (HTTPS/TLS)
            <br />
            • Data at rest is encrypted using Google Cloud encryption
            <br />
            • Access to your data is restricted to essential AI processing
            <br />
            • Regular security audits and updates
            <br />
            • Firebase Authentication for secure account access
          </Typography>

          {/* Third-Party Services */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            5. Third-Party Services
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We use the following third-party services:
            <br />
            • <strong>Google Firebase:</strong> Authentication, database, and hosting
            <br />
            • <strong>Stripe:</strong> Payment processing (PCI-DSS compliant)
            <br />
            • <strong>OpenAI & Anthropic:</strong> AI analysis (your data is not used to train their models)
            <br />
            • <strong>Google Sign-In:</strong> Authentication (covered by Google's privacy policy)
          </Typography>

          {/* Data Sharing */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            6. Data Sharing and Disclosure
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We <strong>DO NOT</strong> sell, rent, or share your personal data with third parties for marketing purposes.
            <br /><br />
            We may share data only:
            <br />
            • With AI providers (OpenAI, Anthropic) for report generation - they do not retain your data
            <br />
            • When required by law or to protect our legal rights
            <br />
            • To prevent fraud or security threats
            <br />
            • With your explicit consent
          </Typography>

          {/* Your Rights */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            7. Your Rights
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            You have the right to:
            <br />
            • <strong>Access:</strong> Request a copy of your data
            <br />
            • <strong>Delete:</strong> Delete your reports and account at any time
            <br />
            • <strong>Correct:</strong> Update your profile information
            <br />
            • <strong>Export:</strong> Download your reports as PDF
            <br />
            • <strong>Object:</strong> Opt out of data processing (may limit service functionality)
            <br />
            • <strong>Withdraw Consent:</strong> Delete your account to revoke all data access
          </Typography>

          {/* Data Retention */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            8. Data Retention
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            • <strong>Chat Logs:</strong> Deleted after 30 days or after regeneration
            <br />
            • <strong>Reports:</strong> Stored until you delete them
            <br />
            • <strong>Account Data:</strong> Retained until account deletion
            <br />
            • <strong>Payment Records:</strong> Retained for 7 years for tax/legal compliance
          </Typography>

          {/* Cookies */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            9. Cookies and Tracking
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We use essential cookies to:
            <br />
            • Maintain your login session
            <br />
            • Remember your preferences
            <br />
            • Analyze site performance (anonymized)
            <br /><br />
            You can disable cookies in your browser, but some features may not work properly.
          </Typography>

          {/* Children's Privacy */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            10. Children's Privacy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            Our service is not intended for users under 18 years of age. We do not knowingly collect data from children. 
            If you believe we have collected data from a minor, please contact us immediately.
          </Typography>

          {/* International Users */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            11. International Users
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            Your data may be processed in the United States where our servers are located. By using our service, 
            you consent to the transfer of your data to the US. We comply with applicable data protection laws.
          </Typography>

          {/* Changes to Policy */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            12. Changes to This Policy
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            We may update this privacy policy from time to time. We will notify you of significant changes by email 
            or through a prominent notice on our platform. Your continued use after changes constitutes acceptance.
          </Typography>

          {/* Contact */}
          <Typography variant="h5" sx={{ mt: 4, mb: 2, color: '#fafafa', fontWeight: 600 }}>
            13. Contact Us
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, color: '#a1a1aa', lineHeight: 1.8 }}>
            If you have questions about this privacy policy or wish to exercise your rights, contact us:
            <br /><br />
            <strong>Support:</strong> Use the in-app messaging system (click "Contact Support" in the footer or visit your Inbox)
            <br />
            <strong>Website:</strong> https://neurolnet.ai
            <br /><br />
            Our AI support agent is available 24/7 to answer your questions and help with requests.
          </Typography>

          {/* Legal Disclaimer */}
          <Box sx={{ 
            mt: 5, 
            p: 3, 
            bgcolor: '#0a0a0a', 
            border: '1px solid #9D00FF',
            borderRadius: '8px'
          }}>
            <Typography variant="body2" sx={{ color: '#a1a1aa', lineHeight: 1.8 }}>
              <strong style={{ color: '#9D00FF' }}>Important:</strong> This cognitive analysis service is for 
              informational and personal insight purposes only. It is not a medical, psychological, or clinical assessment. 
              Reports should not be used for diagnosis, treatment, employment decisions, or legal purposes.
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default PrivacyPolicy;

